import type { SVGProps } from 'react';

import { cn } from '../lib/cn';
import { BRAND_PATHS, BRAND_VIEWBOX, brandWidth } from './paths';

/**
 * `brand` is the beeldmerk in brand green — on light and on the dark rail.
 * `onGreen` is white, for a green tile (the app icon). `current` takes the
 * colour of the text around it, for the small "via Smartkeur" line in a footer
 * that belongs to someone else's house style.
 */
export type BrandIconTone = 'brand' | 'onGreen' | 'current';

const toneFill: Record<BrandIconTone, string> = {
    brand: 'fill-[var(--brand-green)]',
    onGreen: 'fill-[var(--brand-on-green)]',
    current: 'fill-current',
};

export type BrandIconProps = Omit<SVGProps<SVGSVGElement>, 'children' | 'fill' | 'viewBox'> & {
    /** Height in px. The width follows from the icon's proportions. */
    size?: number;
    tone?: BrandIconTone;
    /**
     * Accessible name. Without one the icon is decorative and hidden from
     * assistive technology — give it one only when nothing next to it says
     * "Smartkeur".
     */
    title?: string;
};

/** The Smartkeur beeldmerk: a house with a check. */
export function BrandIcon({
    size = 20,
    tone = 'brand',
    title,
    className,
    ...rest
}: BrandIconProps) {
    const { width, height } = BRAND_VIEWBOX.icon;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${width} ${height}`}
            width={brandWidth(BRAND_VIEWBOX.icon, size)}
            height={size}
            focusable="false"
            {...(title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })}
            className={cn('block shrink-0', toneFill[tone], className)}
            {...rest}
        >
            <path d={BRAND_PATHS.icon} />
        </svg>
    );
}
